import React, { Component, type ReactNode } from 'react';
import html2canvas from 'html2canvas';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  screenshot: string | null;
  capturing: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, screenshot: null, capturing: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught:', error, info.componentStack);
    this.captureScreenshot();
  }

  captureScreenshot = async () => {
    this.setState({ capturing: true });
    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        scale: 1,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
      });
      this.setState({ screenshot: canvas.toDataURL('image/png'), capturing: false });
    } catch {
      this.setState({ capturing: false });
    }
  };

  downloadScreenshot = () => {
    const { screenshot } = this.state;
    if (!screenshot) return;
    const a = document.createElement('a');
    a.href = screenshot;
    a.download = `error-screenshot-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.png`;
    a.click();
  };

  copyErrorDetails = () => {
    const { error } = this.state;
    const details = [
      `Error: ${error?.message || 'Unknown error'}`,
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      `UserAgent: ${navigator.userAgent}`,
      error?.stack ? `\nStack:\n${error.stack}` : '',
    ].join('\n');
    navigator.clipboard.writeText(details).catch(() => {});
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, screenshot: null, capturing: false });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      const { error, screenshot, capturing } = this.state;
      return (
        <div style={{
          minHeight: 300, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', padding: '2rem',
          background: '#fef2f2', borderRadius: 12, margin: '1rem',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚠️</div>
          <h3 style={{ color: '#991b1b', margin: '0 0 0.5rem', fontSize: '1.1rem' }}>
            Something went wrong
          </h3>
          <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center', maxWidth: 400 }}>
            {error?.message || 'An unexpected error occurred'}
          </p>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '1rem' }}>
            <button onClick={this.handleReset} style={btnStyle('#2563eb')}>Try Again</button>
            <button onClick={this.copyErrorDetails} style={btnStyle('#6366f1')}>📋 Copy Error Details</button>
            {screenshot && (
              <button onClick={this.downloadScreenshot} style={btnStyle('#059669')}>📸 Download Screenshot</button>
            )}
            {capturing && <span style={{ fontSize: '0.8rem', color: '#6b7280', alignSelf: 'center' }}>Capturing screenshot...</span>}
          </div>

          {screenshot && (
            <div style={{ marginTop: '0.5rem', maxWidth: 500, width: '100%' }}>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.25rem' }}>Page screenshot at time of error:</p>
              <img src={screenshot} alt="Error screenshot" style={{ width: '100%', borderRadius: 8, border: '1px solid #e5e7eb' }} />
            </div>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

function btnStyle(bg: string): React.CSSProperties {
  return { background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: '0.5rem 1.25rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' };
}

export default ErrorBoundary;
